/**
 * `Contracts` — **F18**: authoring with its mandatory dry-run, the quarantine queue, the
 * attestations, and the merge-conflict review.
 *
 * Four sitemap nodes on one page, anchored, for the reason F17's three are: **a dry-run is
 * meaningless away from the rules it previews**, and a held row is meaningless away from the rule
 * that held it. The nodes stay in the sitemap; `#/contracts/quarantine` and `#/loads` resolve here.
 *
 * ── The two rules that make these screens unlike every other list in the product ──────────────
 *
 * ⚠️ **A held-row count is NOT an error.** Row-level quarantine is the design
 * ([ADR-021](../../../../docs/platform/11-technical-architecture.md#8--architecture-decision-records)),
 * so the success state of a gated load is *"1,200 delivered, 340 held"* — a partial **by design**.
 * Rendering that in an error tone would teach clients that the gate breaks their loads, which is the
 * opposite of the product's claim.
 *
 * ⚠️ **This is never a row editor.** A quarantined row holds references plus the failing field only
 * ([Q40](../../../../docs/process/discovery-2026-07-26-open-questions.md)) — there is no full row
 * here to edit, deliberately, because a second copy of customer PII would need its own encryption,
 * retention, erasure and crypto-shred story. The screen shows what failed and offers a re-drive; it
 * never offers a text box over the client's data.
 *
 * ⚠️ **The merge-review queue shows ONE approver, and says so.** [08 §6.3](../../../../docs/platform/08-information-architecture.md)
 * and ADR-044 asked for two sign-offs; **ADR-077 narrowed that to a single Admin** (owner decision).
 * The screen must not imply a second reviewer who does not exist — so it states the narrowing rather
 * than rendering an empty "awaiting second sign-off" slot that nothing can ever fill.
 */
import { useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  DataTable,
  EmptyState,
  FieldGroup,
  FormulaBlock,
  HelpText,
  Input,
  PageHeader,
  Select,
  Skeleton,
  StatusPill,
  Textarea,
  type DataColumn,
} from '@dedupr/ui';
import { ErrorState, useQuery } from '@dedupr/app-kit';
import { authorized, readJson } from '../planeFetch.js';
import './Contracts.css';

interface Rule {
  id: string;
  description: string;
  predicate: Record<string, unknown>;
}

interface Contract {
  id: string;
  version: number;
  object_type: string;
  rules: Rule[];
  fail_mode: 'quarantine' | 'reject_batch';
  state: 'draft' | 'active' | 'superseded';
  activated_by?: string | null;
  activated_at?: string | null;
  dry_run?: { would_hold: number; rows_evaluated: number; basis: string } | null;
}

interface ContractList {
  object_type: string;
  contracts: Contract[];
  gate_active: boolean;
  note: string;
}

interface Held {
  id: string;
  source_record_id: string;
  rule_id: string;
  field: string;
  field_value: string | null;
  reason: string;
  held_at: string;
  expires_at: string;
}

interface Attestation {
  id: string;
  destination_id: string;
  object_type: string;
  contract: string;
  outcome: string;
  rows_evaluated: number;
  rows_loaded: number;
  rows_held: number;
  complete: boolean;
  digest: string;
  created_at: string;
  body: { statement: string; rules_applied: { id: string; description: string; check: string }[] };
  /** B-177. Optional so an older Worker renders without the control rather than crashing. */
  document_url?: string;
}

interface Conflict {
  id: string;
  proposal_ref: string;
  state: 'flagged' | 'approved' | 'rejected';
  violations: { rule_id: string; description: string; field: string; current_value: string | null }[];
  decision_reason?: string | null;
  flagged_at: string;
}

const PREDICATES = [
  { value: 'required', label: 'must not be empty' },
  { value: 'format:email', label: 'must be a well-formed email' },
  { value: 'format:url', label: 'must be a well-formed URL' },
  { value: 'format:phone', label: 'must be a well-formed phone number' },
  { value: 'format:iso_date', label: 'must be an ISO date' },
  { value: 'format:iso_country', label: 'must be a two-letter country code' },
  { value: 'pii_classified', label: 'must carry a PII classification' },
  { value: 'preserve', label: 'must not be cleared by a merge' },
];

export function Contracts({ orgId }: { orgId?: string }) {
  const [field, setField] = useState('email');
  const [predicate, setPredicate] = useState('format:email');
  const [why, setWhy] = useState('');
  const [pending, setPending] = useState<Rule[]>([]);
  const [refusal, setRefusal] = useState<{ detail: string; fields?: string[] } | null>(null);
  const [preview, setPreview] = useState<{ would_hold: number; rows_evaluated: number; basis: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const contracts = useQuery<ContractList | null>(async () => {
    if (!orgId) return null;
    return readJson<ContractList>(
      await authorized(`/v1/contracts?org_id=${encodeURIComponent(orgId)}&object_type=contact`),
      'The contracts'
    );
  }, [orgId]);

  const quarantine = useQuery<{ held: Held[]; retention_days: number; note: string } | null>(async () => {
    if (!orgId) return null;
    return readJson(await authorized(`/v1/quarantine?org_id=${encodeURIComponent(orgId)}`), 'The quarantine queue');
  }, [orgId]);

  const attestations = useQuery<{ attestations: Attestation[]; note: string } | null>(async () => {
    if (!orgId) return null;
    return readJson(await authorized(`/v1/attestations?org_id=${encodeURIComponent(orgId)}`), 'The attestations');
  }, [orgId]);

  const conflicts = useQuery<{ reviews: Conflict[]; note: string } | null>(async () => {
    if (!orgId) return null;
    return readJson(await authorized(`/v1/merge-gate?org_id=${encodeURIComponent(orgId)}`), 'The conflict queue');
  }, [orgId]);

  if (!orgId) {
    return (
      <EmptyState
        title="No organisation in scope"
        description="A data contract belongs to one organisation. Sign in to one to author or review contracts."
      />
    );
  }
  if (contracts.status === 'loading') {
    return (
      <div className="contracts" aria-busy="true">
        <Skeleton />
        <Skeleton />
      </div>
    );
  }
  if (contracts.status === 'error') {
    return <ErrorState code={contracts.errorCode} message={contracts.errorMessage} onRetry={contracts.reload} />;
  }
  const list = contracts.data;
  if (!list) return <Skeleton />;

  function addRule() {
    const [kind, format] = predicate.split(':');
    setPending([
      ...pending,
      {
        id: `${field}_${kind}${format ? `_${format}` : ''}`,
        description: why.trim() || `${field} ${PREDICATES.find((p) => p.value === predicate)?.label ?? ''}`,
        predicate: kind === 'format' ? { kind, field, format } : { kind, field },
      },
    ]);
    setWhy('');
    setPreview(null);
  }

  async function submit(activate: boolean) {
    setBusy(true);
    setRefusal(null);
    try {
      const response = await authorized(`/v1/contracts?org_id=${encodeURIComponent(orgId!)}`, {
        method: 'POST',
        body: JSON.stringify({ object_type: 'contact', rules: pending, activate }),
      });
      const body = (await response.json().catch(() => null)) as
        | { detail?: string; available_fields?: string[]; dry_run?: { would_hold: number; rows_evaluated: number; basis: string } }
        | null;
      if (!response.ok) {
        /* ⚠️ The server's own words, and its field list. "No field called emial" plus the fields that
           do exist is the single most useful pair of sentences this screen can show. */
        setRefusal({ detail: body?.detail ?? `Refused (${response.status}).`, fields: body?.available_fields });
        return;
      }
      setPreview(body?.dry_run ?? null);
      if (activate) {
        setPending([]);
        contracts.reload();
        attestations.reload();
      }
    } finally {
      setBusy(false);
    }
  }

  async function decideConflict(id: string, decision: 'approve' | 'reject') {
    /* ⚠️ The reason is asked for BEFORE the request, and no reason means no request. The server
       refuses without one anyway; not sending it means somebody who thought better of it does not
       meet an error they did not cause. */
    const reason = window.prompt(
      decision === 'approve'
        ? 'Why is this merge being released despite the contract? With one approver rather than two, this is the only record of why.'
        : 'Why is this merge being rejected?'
    );
    if (!reason || !reason.trim()) return;
    await authorized(`/v1/merge-gate?org_id=${encodeURIComponent(orgId!)}`, {
      method: 'PATCH',
      body: JSON.stringify({ id, decision, reason: reason.trim() }),
    });
    conflicts.reload();
  }

  async function resolveHeld(id: string, action: 'redrive' | 'dismiss', reason?: string) {
    await authorized(`/v1/quarantine?org_id=${encodeURIComponent(orgId!)}`, {
      method: 'POST',
      body: JSON.stringify({ id, action, ...(reason ? { reason } : {}) }),
    });
    quarantine.reload();
  }

  const heldColumns: DataColumn<Held>[] = [
    { id: 'rule', header: 'Held by', kind: 'primary', width: 1.6, cell: (row) => row.rule_id },
    { id: 'field', header: 'Field', width: 1, cell: (row) => row.field },
    {
      id: 'value',
      header: 'Value',
      width: 1.4,
      /* ⚠️ The failing field only. There is no full row here — see the module header. */
      cell: (row) => (row.field_value === null ? <span className="contracts__muted">withheld by policy</span> : row.field_value),
    },
    { id: 'why', header: 'Why', width: 2.4, cell: (row) => row.reason },
    {
      id: 'expires',
      header: 'Expires',
      width: 1.2,
      cell: (row) => `expires ${new Date(row.expires_at).toLocaleDateString()}`,
    },
  ];

  return (
    <div className="contracts">
      <PageHeader
        title="Data contracts"
        description="What is allowed into a destination — and what is held back, with the reason attached."
      />

      {!list.gate_active ? (
        /* ⚠️ Stated, not inferred from an empty list. "No contract" means loads are UNJUDGED, which is
           a different thing from loads that passed — and the difference is the whole feature. */
        <Callout tone="info" title="No gate is active for contacts">
          Nothing is being checked before it reaches a destination, so exports go out unjudged rather
          than clean. Author a contract below; you will see exactly what it would have held before you
          can turn it on.
        </Callout>
      ) : null}

      {/* ── Authoring + the mandatory dry-run ─────────────────────────────────────────────────── */}
      <Card headingLevel={2} title="Author a contract">
        {refusal ? (
          <Callout tone="danger" title="Not accepted">
            <p>{refusal.detail}</p>
            {refusal.fields && refusal.fields.length > 0 ? (
              <p>
                Fields available on this object: <strong>{refusal.fields.join(', ')}</strong>
              </p>
            ) : null}
          </Callout>
        ) : null}

        <FieldGroup label="Field">
          <Input value={field} onChange={(event) => setField(event.currentTarget.value)} placeholder="email" />
        </FieldGroup>
        <FieldGroup label="Rule">
          <Select value={predicate} onChange={(event) => setPredicate(event.currentTarget.value)}>
            {PREDICATES.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Select>
        </FieldGroup>
        <FieldGroup
          label="Why this matters"
          helpText="Shown to whoever finds a row held by this rule. A rule with no reason is a rule nobody can act on."
        >
          <Textarea value={why} onChange={(event) => setWhy(event.currentTarget.value)} rows={2} />
        </FieldGroup>
        <div className="contracts__actions">
          <Button variant="ghost" onClick={addRule} disabled={!field}>
            Add rule
          </Button>
          <Button onClick={() => submit(false)} disabled={busy || pending.length === 0}>
            Preview against my data
          </Button>
          <Button
            onClick={() => submit(true)}
            /* ⚠️ **Activation is gated on the preview having been seen.** The server refuses without a
               dry-run either way; disabling here means the client never meets that refusal by accident,
               and the ordering reads as the workflow it is rather than as a validation error. */
            disabled={busy || pending.length === 0 || preview === null}
          >
            Activate
          </Button>
        </div>
        {pending.length > 0 ? (
          <ul className="contracts__pending">
            {pending.map((rule) => (
              <li key={rule.id}>
                <strong>{rule.id}</strong> — {rule.description}
              </li>
            ))}
          </ul>
        ) : null}
        {preview ? (
          <Callout
            /* ⚠️ Neutral, not a warning. "Would hold 340" is information about the rules, not a fault. */
            tone="info"
            title={`This contract would hold ${preview.would_hold.toLocaleString()} of ${preview.rows_evaluated.toLocaleString()} rows`}
          >
            {preview.basis}
          </Callout>
        ) : (
          <HelpText>
            A contract cannot be activated until you have previewed it. A gate installed without anyone
            seeing what it stops is the one thing activation must not allow.
          </HelpText>
        )}
      </Card>

      {/* ── Versions ─────────────────────────────────────────────────────────────────────────── */}
      <Card headingLevel={2} title="Versions">
        <HelpText>{list.note}</HelpText>
        <DataTable
          label="Contract versions"
          rows={list.contracts}
          rowKey={(row) => row.id}
          empty="No contract has been authored for this object yet."
          columns={[
            { id: 'version', header: 'Version', kind: 'primary', width: 0.8, cell: (row) => `v${row.version}` },
            {
              id: 'state',
              header: 'State',
              width: 1,
              cell: (row) => (
                <StatusPill tone={row.state === 'active' ? 'success' : 'neutral'} description={`Contract version ${row.version} is ${row.state}`}>
                  {row.state}
                </StatusPill>
              ),
            },
            { id: 'rules', header: 'Rules', width: 0.8, cell: (row) => String(row.rules.length) },
            { id: 'mode', header: 'On failure', width: 1.2, cell: (row) => (row.fail_mode === 'quarantine' ? 'hold the row' : 'reject the batch') },
            { id: 'by', header: 'Activated by', width: 1.4, cell: (row) => row.activated_by ?? '—' },
            {
              id: 'preview',
              header: 'Preview at activation',
              width: 1.6,
              /* ⚠️ BR-8's evidence, on screen. A year later, "did anyone look?" has an answer here. */
              cell: (row) =>
                row.dry_run ? `${row.dry_run.would_hold.toLocaleString()} would have been held` : '—',
            },
          ]}
        />
      </Card>

      {/* ── Quarantine ───────────────────────────────────────────────────────────────────────── */}
      <div id="quarantine">
        <Card headingLevel={2} title="Held rows">
          {quarantine.status === 'error' ? (
            <ErrorState code={quarantine.errorCode} message={quarantine.errorMessage} onRetry={quarantine.reload} />
          ) : null}
          {quarantine.data ? <HelpText>{quarantine.data.note}</HelpText> : null}
          <DataTable
            label="Rows held by a contract rule"
            rows={quarantine.data?.held ?? []}
            rowKey={(row) => row.id}
            empty="Nothing is being held. Rows that fail a rule appear here with the reason attached."
            columns={heldColumns}
            expanded={(row) => (
              <div className="contracts__detail">
                <p>
                  Record <strong>{row.source_record_id}</strong>, held {new Date(row.held_at).toLocaleString()}.
                </p>
                <HelpText>
                  Re-driving does not replay a stored copy — the row is re-derived from staging on the
                  next export, so if the source has changed since, it is the current row that gets
                  evaluated.
                </HelpText>
                <div className="contracts__actions">
                  <Button onClick={() => resolveHeld(row.id, 'redrive')}>Re-drive on the next export</Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      const reason = window.prompt('Why is this row being dismissed?');
                      /* ⚠️ No reason ⇒ no request. The server refuses it anyway; not sending it means
                         somebody who changed their mind does not meet an error they did not cause. */
                      if (reason && reason.trim()) resolveHeld(row.id, 'dismiss', reason.trim());
                    }}
                  >
                    Dismiss with a reason
                  </Button>
                </div>
              </div>
            )}
          />
        </Card>
      </div>

      {/* ── Attestations ─────────────────────────────────────────────────────────────────────── */}
      <div id="loads">
        <Card headingLevel={2} title="Load attestations">
          {attestations.data ? <HelpText>{attestations.data.note}</HelpText> : null}
          <DataTable
            label="Attestations per gated load"
            rows={attestations.data?.attestations ?? []}
            rowKey={(row) => row.id}
            empty="No gated load has run yet. One attestation is written per load, including the clean ones."
            columns={[
              { id: 'when', header: 'When', kind: 'primary', width: 1.4, cell: (row) => new Date(row.created_at).toLocaleString() },
              { id: 'dest', header: 'Destination', width: 1.2, cell: (row) => row.destination_id },
              { id: 'contract', header: 'Contract', width: 1.4, cell: (row) => row.contract },
              {
                id: 'counts',
                header: 'Delivered / held',
                width: 2,
                /* ⚠️ **The unit is IN the cell, because an expandable table renders no header row** —
                   found in a browser, where this column read as a bare "1,238 / 2". The catalogue's
                   disclosure shape is header-less by design (the ui-spec asks for it), so a column
                   whose meaning lives in its header has no meaning at all here.
                   ⚠️ **Never an error tone.** A partial load is the design, not a failure — a held
                   count in red teaches clients that the gate breaks their loads. */
                cell: (row) => (
                  <>
                    {row.rows_loaded.toLocaleString()} delivered · {row.rows_held.toLocaleString()} held
                  </>
                ),
              },
              {
                id: 'complete',
                header: 'Complete',
                width: 1,
                cell: (row) =>
                  row.complete ? <Badge variant="success">complete</Badge> : <Badge variant="neutral">partial</Badge>,
              },
            ]}
            expanded={(row) => (
              <div className="contracts__detail">
                <p>{row.body.statement}</p>
                <FormulaBlock label="Rules applied to this load" wrap="path">
                  {row.body.rules_applied.map((r) => `${r.id}: ${r.check}`).join('\n')}
                </FormulaBlock>
                <HelpText>
                  Digest <code>{row.digest.slice(0, 16)}…</code> — an evidence pack uses it to show this
                  attestation has not been altered since it was written.
                </HelpText>
                {/* ⚠️ **B-177: an auditor cannot be handed a table in a web app.** The document is the
                    deliverable; the row is how you find it. The download is itself written to the
                    audit trail, because taking compliance evidence out of the product is an event. */}
                {row.document_url ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      void (async () => {
                        /* `authorized` is the app's one place that attaches the session — a second
                           hand-rolled fetch here would be a second place to get it wrong. */
                        const response = await authorized(row.document_url!);
                        const blob = await response.blob();
                        const link = document.createElement('a');
                        link.href = URL.createObjectURL(blob);
                        link.download = `dedupr-attestation-${row.id}.json`;
                        link.click();
                        URL.revokeObjectURL(link.href);
                      })()
                    }
                  >
                    Download this attestation
                  </Button>
                ) : null}
              </div>
            )}
          />
        </Card>
      </div>

      {/* ── Merge conflict review ────────────────────────────────────────────────────────────── */}
      <div id="merge-review">
        <Card headingLevel={2} title="Merges held by a contract">
          {conflicts.data ? <HelpText>{conflicts.data.note}</HelpText> : null}
          <DataTable
            label="Merges pulled out of the ordinary approval queue"
            rows={conflicts.data?.reviews ?? []}
            rowKey={(row) => row.id}
            empty="No pending merge would violate the active contract."
            columns={[
              { id: 'proposal', header: 'Merge', kind: 'primary', width: 1.6, cell: (row) => row.proposal_ref },
              {
                id: 'state',
                header: 'State',
                width: 1,
                cell: (row) => (
                  <StatusPill
                    tone={row.state === 'flagged' ? 'warning' : row.state === 'approved' ? 'success' : 'neutral'}
                    description={`This merge review is ${row.state}`}
                  >
                    {row.state}
                  </StatusPill>
                ),
              },
              {
                id: 'count',
                header: 'Rules violated',
                width: 1.4,
                /* Same header-less reason as above — a bare "1" says nothing. */
                cell: (row) => `${row.violations.length} rule${row.violations.length === 1 ? '' : 's'} violated`,
              },
              {
                id: 'when',
                header: 'Flagged',
                width: 1.4,
                cell: (row) => `flagged ${new Date(row.flagged_at).toLocaleDateString()}`,
              },
            ]}
            expanded={(row) => (
              <div className="contracts__detail">
                {/* ⚠️ The values at risk, BEFORE any approve control. That ordering is the whole point
                    of the third enforcement point — at load time these would already be gone. */}
                <ul className="contracts__violations">
                  {row.violations.map((v) => (
                    <li key={v.rule_id}>
                      <strong>{v.field}</strong> — {v.description}
                      {v.current_value ? (
                        <>
                          {' '}
                          Would discard: <strong>{v.current_value}</strong>
                        </>
                      ) : null}
                    </li>
                  ))}
                </ul>
                {row.state === 'flagged' ? (
                  <>
                    <Callout tone="warning" title="One Admin releases this">
                      ADR-044 asked for two sign-offs; a single Admin approval releases it today, with a
                      mandatory reason (ADR-077). The values listed above are the whole of the second
                      opinion — there is no second reviewer waiting.
                    </Callout>
                    {/* ⚠️ The controls come AFTER the values at risk and after that sentence, in
                        source order as well as visually. A review whose approve button is reachable
                        before the reader has passed what it destroys is the ordinary approval path
                        this queue exists to interrupt. */}
                    <div className="contracts__actions">
                      <Button onClick={() => decideConflict(row.id, 'approve')}>Release with a reason</Button>
                      <Button variant="ghost" onClick={() => decideConflict(row.id, 'reject')}>
                        Reject this merge
                      </Button>
                    </div>
                  </>
                ) : (
                  <HelpText>
                    {row.state === 'approved' ? 'Released' : 'Rejected'}
                    {row.decision_reason ? `: ${row.decision_reason}` : ''}
                  </HelpText>
                )}
              </div>
            )}
          />
        </Card>
      </div>
    </div>
  );
}
